import React, { useEffect, useRef } from "react";
import gsap from "gsap";
import "./styles/loading.scss";

/**
 * INTRO_CONFIG
 * Graceful, cinematic timings for the iconic P intro:
 * 1. P outline is drawn (SVG stroke-dashoffset)
 * 2. Outline is filled with vibrant cyan
 * 3. Camera pushes forward in 3D through the P counter hole
 * 4. Portfolio emerges seamlessly in 3D from depth
 */
export const INTRO_CONFIG = {
  strokeDuration: 1.35,           // Slow, graceful outline draw (~1.35s)
  fillStart: 1.05,               // Fill starts as outline finishes
  fillDuration: 0.55,            // Cyan flood fill duration (~0.55s)
  holdDuration: 0.35,            // Hold on the fully filled P monogram
  pushStart: 1.95,               // Camera 3D push begins at 1.95s
  pushDuration: 0.95,            // Smooth camera push through counter hole
  pushScale: 24,                 // 3D zoom scale factor
  counterOrigin: "49.88% 29.95%", // Optical center of inner bowl: X = 61.94, Y = 30.13
  emergeStart: 2.15,             // 3D Hero emerges from depth
  emergeDuration: 0.95,          // Hero emergence duration
  safetyTimeoutMs: 4500,         // Fallback safety timeout
};

function Loading({ onFinish }) {
  const isFinishedRef = useRef(false);
  const masterTimelineRef = useRef(null);

  useEffect(() => {
    // If user prefers reduced motion, skip intro immediately
    if (
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      const el = document.getElementById("loading");
      if (el) el.style.display = "none";
      if (onFinish) onFinish();
      return;
    }

    // Guard: already seen in this session
    if (
      typeof window !== "undefined" &&
      sessionStorage.getItem("portfolio_intro_seen") === "true"
    ) {
      const el = document.getElementById("loading");
      if (el) el.style.display = "none";
      if (onFinish) onFinish();
      return;
    }

    const finish = () => {
      if (isFinishedRef.current) return;
      isFinishedRef.current = true;
      sessionStorage.setItem("portfolio_intro_seen", "true");

      if (masterTimelineRef.current) {
        masterTimelineRef.current.kill();
      }

      // Snap all transforms, perspective, and will-change to clean final states
      const wrapper = document.getElementById("site-3d-wrapper");
      const container = document.getElementById("site-3d-container");
      const navbar = document.getElementById("navbar");
      const loadingEl = document.getElementById("loading");

      if (wrapper) {
        wrapper.style.transform = "none";
        wrapper.style.filter = "none";
        wrapper.style.opacity = "1";
        wrapper.style.willChange = "auto";
      }
      if (container) {
        container.style.perspective = "none";
      }
      if (navbar) {
        navbar.style.opacity = "1";
      }
      if (loadingEl) {
        loadingEl.style.display = "none";
        loadingEl.style.opacity = "0";
        loadingEl.style.pointerEvents = "none";
      }

      document.body.style.overflow = "";

      if (onFinish) onFinish();
    };

    // Hard fallback safety timeout
    const fallbackTimer = setTimeout(() => {
      finish();
    }, INTRO_CONFIG.safetyTimeoutMs);

    // Global skip handler: ANY key, click, or tap
    const handleSkipEvent = () => {
      finish();
    };

    window.addEventListener("keydown", handleSkipEvent);

    // Device / Mobile capabilities check
    const isMobile =
      typeof window !== "undefined" &&
      (window.innerWidth <= 600 ||
        (navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 4));

    const startZ = isMobile ? -140 : -220;
    const startRotX = isMobile ? 3 : 4;
    const startScale = isMobile ? 0.95 : 0.93;

    // Run GSAP timeline inside gsap.context() for StrictMode cleanup
    const ctx = gsap.context(() => {
      // Pre-hide navbar during intro transition
      const navbar = document.getElementById("navbar");
      if (navbar) {
        gsap.set(navbar, { opacity: 0 });
      }

      // Pre-set 3D wrapper state behind overlay (pure GPU transform, no blur)
      const wrapper = document.getElementById("site-3d-wrapper");
      if (wrapper) {
        gsap.set(wrapper, {
          transform: `translate3d(0, 0, ${startZ}px) rotateX(${startRotX}deg) scale(${startScale})`,
          opacity: 0,
          willChange: "transform, opacity",
          force3D: true,
        });
      }

      // Promote animated P elements strictly to compositor layers
      const pSvg = document.querySelector(".loading-p-svg");
      const pPath = document.querySelector(".loading-p-path");
      if (pSvg) {
        gsap.set(pSvg, {
          scale: 1,
          opacity: 1,
          willChange: "transform, opacity",
          force3D: true,
        });
      }
      if (pPath) {
        gsap.set(pPath, {
          strokeDasharray: 380,
          strokeDashoffset: 380,
          fill: "transparent",
        });
      }

      // Master Animation Timeline
      const tl = gsap.timeline({
        onComplete: () => {
          finish();
        },
      });

      masterTimelineRef.current = tl;

      // ==========================================
      // PHASE 1: DRAW P OUTLINE (SVG stroke ~1.35s)
      // ==========================================
      tl.to(
        ".loading-p-path",
        {
          strokeDashoffset: 0,
          duration: INTRO_CONFIG.strokeDuration,
          ease: "power2.inOut",
        },
        0
      );

      // ==========================================
      // PHASE 2: FILL OUTLINE WITH CYAN COLOR (~0.55s)
      // ==========================================
      tl.to(
        ".loading-p-path",
        {
          fill: "#38bdf8",
          duration: INTRO_CONFIG.fillDuration,
          ease: "power1.out",
        },
        INTRO_CONFIG.fillStart
      );

      // ==========================================
      // PHASE 3: 3D ZOOM CAMERA PUSH INTO DOORWAY
      // ==========================================
      const pushInStart = INTRO_CONFIG.pushStart;

      // Skip button fades out
      tl.to(
        ".loading-skip-btn",
        {
          opacity: 0,
          duration: 0.25,
          ease: "power2.out",
        },
        pushInStart
      );

      // Camera pushes forward into the counter hole: scale 1 -> 24
      tl.to(
        ".loading-p-svg",
        {
          scale: INTRO_CONFIG.pushScale,
          transformOrigin: INTRO_CONFIG.counterOrigin,
          duration: INTRO_CONFIG.pushDuration,
          ease: "power3.in",
          force3D: true,
        },
        pushInStart
      );

      // P fades out as camera passes through the opening
      tl.to(
        ".loading-p-svg",
        {
          opacity: 0,
          duration: 0.4,
          ease: "power2.in",
        },
        pushInStart + 0.45
      );

      // Background overlay fades out
      tl.to(
        "#loading",
        {
          opacity: 0,
          duration: 0.45,
          ease: "power2.in",
        },
        pushInStart + 0.45
      );

      // ==========================================
      // PHASE 4: 3D HERO EMERGENCE FROM DEPTH
      // ==========================================
      const emergeStart = INTRO_CONFIG.emergeStart;

      if (wrapper) {
        tl.to(
          wrapper,
          {
            transform: "translate3d(0, 0, 0px) rotateX(0deg) scale(1)",
            opacity: 1,
            duration: INTRO_CONFIG.emergeDuration,
            ease: "power3.out",
            force3D: true,
          },
          emergeStart
        );
      }

      // Navbar fades in smoothly
      if (navbar) {
        tl.to(
          navbar,
          {
            opacity: 1,
            duration: 0.45,
            ease: "power2.out",
          },
          emergeStart + 0.4
        );
      }
    });

    return () => {
      clearTimeout(fallbackTimer);
      window.removeEventListener("keydown", handleSkipEvent);
      ctx.revert();
    };
  }, [onFinish]);

  const handleManualSkip = React.useCallback(
    (e) => {
      if (e && e.stopPropagation) e.stopPropagation();
      if (masterTimelineRef.current) masterTimelineRef.current.kill();
      sessionStorage.setItem("portfolio_intro_seen", "true");
      const el = document.getElementById("loading");
      if (el) el.style.display = "none";
      if (onFinish) onFinish();
    },
    [onFinish]
  );

  const [hasExistingNode] = React.useState(() => {
    return typeof document !== "undefined" && !!document.getElementById("loading");
  });

  // Attach click listener to existing static HTML #loading node
  useEffect(() => {
    const el = document.getElementById("loading");
    if (el) {
      el.addEventListener("click", handleManualSkip);
      return () => el.removeEventListener("click", handleManualSkip);
    }
  }, [handleManualSkip]);

  if (hasExistingNode) {
    return null;
  }

  return (
    <div
      id="loading"
      role="status"
      aria-label="Loading portfolio animation"
      onClick={handleManualSkip}
    >
      <button
        type="button"
        className="loading-skip-btn"
        onClick={handleManualSkip}
        title="Skip intro animation (Click or press any key)"
        aria-label="Skip intro animation"
      >
        <span>Skip</span>
        <span className="key-badge">ESC</span>
      </button>

      <div className="loading-p-wrapper">
        <svg
          className="loading-p-svg"
          width="180"
          height="164"
          viewBox="10 1.75 104.13 94.75"
          fill="transparent"
          overflow="visible"
          xmlns="http://www.w3.org/2000/svg"
          aria-label="P monogram logo"
        >
          <path
            className="loading-p-path"
            d="M10 96.50L10 1.75L83.88 1.75Q98.75 1.75 106.45 8.88Q114.13 16.00 114.13 30.13Q114.13 44.38 106.45 51.38Q98.75 58.38 83.88 58.38L30 58.38L30 96.50L10 96.50M82.50 18.13L30 18.13L30 42.00L82.50 42.13Q88.00 42.13 90.95 38.88Q93.88 35.63 93.88 30.13Q93.88 24.50 90.95 21.31Q88.00 18.13 82.50 18.13Z"
            stroke="#38bdf8"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="transparent"
          />
        </svg>
      </div>
    </div>
  );
}

export default Loading;
